import {
	MessageConditionSingle,
	MessageConditionAuto,
	MessageCondition,
	MessageChannel,
	IntervalCondition,
} from '../types'

/**
 * Labels for message conditions in Thai
 * Covers both manual/scheduled message conditions and auto message conditions
 */
export const MESSAGE_CONDITION_LABELS: Record<MessageCondition, string> = {
	// Manual/Scheduled Message Conditions (MessageConditionSingle)
	[MessageConditionSingle.REGISTER_DAYS]:
		'สมาชิกใหม่ตามจำนวนวัน (สมัครภายใน X วันที่ผ่านมา)',
	[MessageConditionSingle.REGISTER_DAYS_NEVER_DEPOSIT]:
		'สมาชิกใหม่ที่ไม่เคยฝาก (สมัครภายใน X วันที่ผ่านมา)',
	[MessageConditionSingle.INACTIVE_DAYS_SINGLE_MESSAGE]:
		'ไม่มีการเข้าใช้งานเป็นเวลา X วันหรือมากกว่า',
	[MessageConditionSingle.DEPOSIT_RANGE]: 'ช่วงยอดฝาก (ฝากภายใน X ถึง Y บาท)',
	[MessageConditionSingle.ALL_MEMBERS]: 'สมาชิกทั้งหมด',
	[MessageConditionSingle.ALL_MEMBERS_NEVER_DEPOSIT]: 'สมาชิกที่ไม่เคยฝาก',
	[MessageConditionSingle.ALL_MEMBERS_TOTAL_DEPOSIT]: 'สมาชิกตามยอดฝากสะสม',
	[MessageConditionSingle.ALL_MEMBERS_SINGLE_DEPOSIT]: 'สมาชิกตามยอดฝากต่อบิล',
	[MessageConditionSingle.ALL_MEMBERS_TOTAL_TURNOVER]: 'สมาชิกตามยอดเทิร์นสะสม',
	[MessageConditionSingle.ALL_MEMBERS_TOTAL_LOSS]: 'สมาชิกตามยอดเสียสะสม',
	[MessageConditionSingle.PENDING_INVITE_FRIEND_BALANCE]:
		'สมาชิกที่มียอดคงค้างจากแนะนำเพื่อน',
	[MessageConditionSingle.PENDING_RETURN_LOSS_BALANCE]:
		'สมาชิกที่มียอดคงค้างจากคืนยอดเสีย',
	[MessageConditionSingle.PENDING_COMMISSION_BALANCE]:
		'สมาชิกที่มียอดคงค้างจากค่าคอมมิชชั่น',
	[MessageConditionSingle.PENDING_DAILY_CHECK_IN_BALANCE]:
		'สมาชิกที่มียอดคงค้างจากเช็คอินรายวัน',
	[MessageConditionSingle.PENDING_LUCKY_WHEEL_BALANCE]:
		'สมาชิกที่มียอดคงค้างจากวงล้อนำโชค',
	[MessageConditionSingle.LINE_LINKED_DAYS]:
		'สมาชิกที่เชื่อมต่อ LINE ภายใน X วันที่ผ่านมา',
	[MessageConditionSingle.SPECIFIC_PHONE_NUMBERS]: 'ระบุเบอร์โทรศัพท์เอง',
	[MessageConditionSingle.CUSTOMER_TAGS]: 'สมาชิกตามแท็ก (Customer Tags)',

	// Auto Message Conditions (MessageConditionAuto)
	[MessageConditionAuto.NO_DEPOSIT_AFTER_REGISTER_MINUTES]:
		'สมัครเสร็จแล้วแต่ไม่มีการฝากภายใน X นาที',
	[MessageConditionAuto.REGISTER_MINUTES]:
		'หลังสมัครสมาชิกสำเร็จเป็นเวลา X นาที',
	[MessageConditionAuto.INACTIVE_DAYS_AUTO_MESSAGE]:
		'ไม่มีการเข้าใช้งานเป็นเวลาครบ X วัน',
	[MessageConditionAuto.LINE_CONNECTED_NO_DEPOSIT_MINUTES]:
		'เชื่อมต่อ LINE แล้วแต่ไม่มีการฝากภายใน X นาที',
	[MessageConditionAuto.BALANCE_DEPLETED_NO_DEPOSIT]:
		'เงินหมดและไม่มีการฝากภายใน X นาที',
	[MessageConditionAuto.CONSECUTIVE_PLAY_DAYS_GT_X]:
		'เล่นติดต่อกันครบ X วันหรือมากกว่า',
}

/**
 * Get Thai label for a message condition
 * @param conditionName - The condition enum value
 * @returns Thai label for the condition, or the condition name if not found
 */
export function getConditionLabel(conditionName: string): string {
	return (
		MESSAGE_CONDITION_LABELS[conditionName as MessageCondition] || conditionName
	)
}

/**
 * Labels for message channels in Thai/English
 */
export const CHANNEL_LABELS: Record<MessageChannel, string> = {
	[MessageChannel.SMS]: 'SMS',
	[MessageChannel.LINE]: 'LINE',
	[MessageChannel.LINE_HOT_GAME]: 'LINE Hot Games',
	[MessageChannel.LINE_PROMOTION]: 'LINE Promotion',
}

/**
 * Get label for a message channel
 * @param channel - The channel enum value
 * @returns Label for the channel, or the channel value if not found
 */
export function getChannelLabel(channel: string): string {
	return CHANNEL_LABELS[channel as MessageChannel] || channel
}

/**
 * Labels for interval conditions in Thai
 */
export const INTERVAL_CONDITION_LABELS: Record<IntervalCondition, string> = {
	[IntervalCondition.DAY_OF_WEEK]: 'ระบุวันในสัปดาห์',
	[IntervalCondition.DATE_OF_MONTH]: 'ระบุวันที่ของเดือน',
}

/**
 * Get Thai label for interval condition
 * @param condition - The interval condition enum value
 * @returns Thai label for the condition, or the condition value if not found
 */
export function getIntervalConditionLabel(condition: string): string {
	return INTERVAL_CONDITION_LABELS[condition as IntervalCondition] || condition
}

/**
 * Thai day of week labels (0 = Sunday, 6 = Saturday)
 */
const DAY_OF_WEEK_LABELS: Record<number, string> = {
	0: 'วันอาทิตย์',
	1: 'วันจันทร์',
	2: 'วันอังคาร',
	3: 'วันพุธ',
	4: 'วันพฤหัสบดี',
	5: 'วันศุกร์',
	6: 'วันเสาร์',
}

/**
 * Get Thai label for day of week
 * @param value - Day of week (0-6, where 0 is Sunday)
 * @returns Thai day name
 */
export function getDayOfWeekLabel(value: number): string {
	return DAY_OF_WEEK_LABELS[value] || `วันที่ ${value}`
}

/**
 * Get formatted date of month label
 * @param value - Date of month (1-31)
 * @returns Formatted date string
 */
export function getDateOfMonthLabel(value: number): string {
	return `วันที่ ${value} ของทุกเดือน`
}

/**
 * Per-condition date range section label (replaces generic "ช่วงวันที่")
 */
export const DATE_RANGE_SECTION_LABELS: Partial<
	Record<MessageConditionSingle, string>
> = {
	[MessageConditionSingle.ALL_MEMBERS]: 'กรองตามวันที่สมัครสมาชิก',
	[MessageConditionSingle.ALL_MEMBERS_NEVER_DEPOSIT]:
		'กรองตามวันที่สมัครสมาชิก',
	[MessageConditionSingle.ALL_MEMBERS_TOTAL_DEPOSIT]: 'กรองตามวันที่ฝาก',
	[MessageConditionSingle.ALL_MEMBERS_SINGLE_DEPOSIT]: 'กรองตามวันที่ฝาก',
	[MessageConditionSingle.ALL_MEMBERS_TOTAL_TURNOVER]:
		'ช่วงเวลาที่คำนวณยอดเทิร์น',
	[MessageConditionSingle.ALL_MEMBERS_TOTAL_LOSS]: 'ช่วงเวลาที่คำนวณยอดเสีย',
	[MessageConditionSingle.DEPOSIT_RANGE]: 'กรองตามวันที่ฝาก',
	[MessageConditionSingle.REGISTER_DAYS]: 'กรองตามวันที่สมัครสมาชิก',
	[MessageConditionSingle.REGISTER_DAYS_NEVER_DEPOSIT]:
		'กรองตามวันที่สมัครสมาชิก',
	[MessageConditionSingle.LINE_LINKED_DAYS]: 'กรองตามวันที่เชื่อมต่อ LINE',
}

/**
 * Get the date range section label for a condition
 */
export function getDateRangeSectionLabel(conditionName: string): string {
	return (
		DATE_RANGE_SECTION_LABELS[conditionName as MessageConditionSingle] ??
		'ช่วงวันที่'
	)
}

/**
 * Per-condition helper text for the date range field
 */
export const DATE_RANGE_HELP_TEXT: Partial<
	Record<MessageConditionSingle, string>
> = {
	[MessageConditionSingle.ALL_MEMBERS]:
		'กรองเฉพาะสมาชิกที่สมัครในช่วงวันที่ที่ระบุ',
	[MessageConditionSingle.ALL_MEMBERS_NEVER_DEPOSIT]:
		'กรองเฉพาะสมาชิกที่สมัครในช่วงวันที่ที่ระบุ',
	[MessageConditionSingle.ALL_MEMBERS_TOTAL_DEPOSIT]:
		'กรองตามวันที่ทำรายการฝาก',
	[MessageConditionSingle.ALL_MEMBERS_SINGLE_DEPOSIT]:
		'กรองตามวันที่ทำรายการฝาก',
	[MessageConditionSingle.ALL_MEMBERS_TOTAL_TURNOVER]:
		'คำนวณยอดเทิร์นที่เกิดขึ้นภายในช่วงวันที่นี้เท่านั้น (ไม่ใช่ยอดสะสมตลอดกาล)',
	[MessageConditionSingle.ALL_MEMBERS_TOTAL_LOSS]:
		'คำนวณยอดเสียที่เกิดขึ้นภายในช่วงวันที่นี้เท่านั้น (ไม่ใช่ยอดสะสมตลอดกาล)',
	[MessageConditionSingle.DEPOSIT_RANGE]: 'กรองตามวันที่ทำรายการฝาก',
	[MessageConditionSingle.REGISTER_DAYS]:
		'ระบุช่วงวันที่สมัครสมาชิก แทนการนับจำนวนวันย้อนหลัง',
	[MessageConditionSingle.REGISTER_DAYS_NEVER_DEPOSIT]:
		'ระบุช่วงวันที่สมัครสมาชิก แทนการนับจำนวนวันย้อนหลัง',
	[MessageConditionSingle.LINE_LINKED_DAYS]:
		'ระบุช่วงวันที่เชื่อมต่อ LINE แทนการนับจำนวนวันย้อนหลัง',
}

/**
 * Get the date range help text for a condition
 */
export function getDateRangeHelpText(conditionName: string): string {
	return (
		DATE_RANGE_HELP_TEXT[conditionName as MessageConditionSingle] ??
		'ระบุช่วงวันที่เพื่อกรองข้อมูลตามเงื่อนไขภายในช่วงเวลาที่กำหนด'
	)
}

/**
 * Per-condition labels for min/max amount inputs
 */
export interface AmountFieldLabels {
	min: string
	max: string
}

export const AMOUNT_FIELD_LABELS: Partial<
	Record<MessageConditionSingle, AmountFieldLabels>
> = {
	[MessageConditionSingle.ALL_MEMBERS_TOTAL_DEPOSIT]: {
		min: 'ยอดฝากสะสมขั้นต่ำ (บาท)',
		max: 'ยอดฝากสะสมสูงสุด (บาท)',
	},
	[MessageConditionSingle.ALL_MEMBERS_SINGLE_DEPOSIT]: {
		min: 'ยอดฝากต่อบิลขั้นต่ำ (บาท)',
		max: 'ยอดฝากต่อบิลสูงสุด (บาท)',
	},
	[MessageConditionSingle.ALL_MEMBERS_TOTAL_TURNOVER]: {
		min: 'ยอดเทิร์นสะสมขั้นต่ำ (บาท)',
		max: 'ยอดเทิร์นสะสมสูงสุด (บาท)',
	},
	[MessageConditionSingle.ALL_MEMBERS_TOTAL_LOSS]: {
		min: 'ยอดเสียสะสมขั้นต่ำ (บาท)',
		max: 'ยอดเสียสะสมสูงสุด (บาท)',
	},
	[MessageConditionSingle.DEPOSIT_RANGE]: {
		min: 'ยอดฝากขั้นต่ำ (บาท)',
		max: 'ยอดฝากสูงสุด (บาท)',
	},
	[MessageConditionSingle.PENDING_INVITE_FRIEND_BALANCE]: {
		min: 'ยอดคงค้างขั้นต่ำ (บาท)',
		max: 'ยอดคงค้างสูงสุด (บาท)',
	},
	[MessageConditionSingle.PENDING_RETURN_LOSS_BALANCE]: {
		min: 'ยอดคงค้างขั้นต่ำ (บาท)',
		max: 'ยอดคงค้างสูงสุด (บาท)',
	},
	[MessageConditionSingle.PENDING_COMMISSION_BALANCE]: {
		min: 'ยอดคงค้างขั้นต่ำ (บาท)',
		max: 'ยอดคงค้างสูงสุด (บาท)',
	},
	[MessageConditionSingle.PENDING_DAILY_CHECK_IN_BALANCE]: {
		min: 'ยอดคงค้างขั้นต่ำ (บาท)',
		max: 'ยอดคงค้างสูงสุด (บาท)',
	},
	[MessageConditionSingle.PENDING_LUCKY_WHEEL_BALANCE]: {
		min: 'ยอดคงค้างขั้นต่ำ (บาท)',
		max: 'ยอดคงค้างสูงสุด (บาท)',
	},
}

/**
 * Get amount field labels for a condition (falls back to generic labels)
 */
export function getAmountFieldLabels(conditionName: string): AmountFieldLabels {
	return (
		AMOUNT_FIELD_LABELS[conditionName as MessageConditionSingle] ?? {
			min: 'ค่าต่ำสุด',
			max: 'ค่าสูงสุด',
		}
	)
}

/**
 * Per-condition label for SINGLE value input
 */
export const SINGLE_VALUE_LABELS: Partial<
	Record<MessageConditionSingle, string>
> = {
	[MessageConditionSingle.INACTIVE_DAYS_SINGLE_MESSAGE]:
		'จำนวนวันที่ไม่มีการเข้าใช้งาน (วัน)',
}

/**
 * Get single value input label for a condition
 */
export function getSingleValueLabel(conditionName: string): string {
	return (
		SINGLE_VALUE_LABELS[conditionName as MessageConditionSingle] ??
		'ค่าที่ต้องการ'
	)
}

/**
 * Get short day of week label (for dropdowns)
 */
export const DAY_OF_WEEK_OPTIONS = [
	{ value: 0, label: 'อาทิตย์' },
	{ value: 1, label: 'จันทร์' },
	{ value: 2, label: 'อังคาร' },
	{ value: 3, label: 'พุธ' },
	{ value: 4, label: 'พฤหัสบดี' },
	{ value: 5, label: 'ศุกร์' },
	{ value: 6, label: 'เสาร์' },
]
